/**
 * Phase 2 MM12 — Compliance / Approval tab (project studio).
 * Case list/create → detail (risk, final gate, missing items) →
 * checklist / claims / language / sign-off / gate / export sections.
 * Every view shows the honesty line: internal decision-support + human
 * sign-off, NOT legal/regulatory/food-safety/supplier/print approval.
 */

import { useCallback, useEffect, useState } from "react";
import {
  createComplianceCase, createComplianceExport, decideComplianceSignoff,
  evaluateComplianceGate, getArtworkPackages, getComplianceCases,
  getComplianceChecklist, getComplianceClaims, getComplianceExports,
  getComplianceGate, getComplianceLanguage, getComplianceSignoffs,
  patchComplianceChecklist, patchComplianceClaim, patchComplianceLanguage,
  requestComplianceSignoff,
} from "../api/client";
import { Button } from "../components/Button";
import { StatusPill } from "../components/StatusPill";
import type { StudioStrings } from "../i18n/studioStrings";

type Row = Record<string, unknown>;
const REVIEW_TYPES = ["launch_readiness_review", "packaging_copy_review",
  "claim_risk_review", "food_label_review", "allergen_review",
  "language_localization_review", "artwork_handoff_review",
  "general_compliance_review"];
const CHK_STATUSES = ["not_started", "missing", "needs_review", "issue_found",
  "accepted", "rejected", "not_applicable"];
const CLAIM_STATUSES = ["draft", "needs_evidence", "needs_rewording",
  "needs_regulatory_review", "accepted_for_internal_draft", "rejected"];
const APPROVAL_TYPES = ["internal_reviewer", "design_reviewer",
  "language_reviewer", "regulatory_reviewer", "owner_signoff"];
const GATES = ["allowed_for_internal_use", "allowed_for_client_preview",
  "allowed_with_warnings"];

export function ComplianceTab({ projectId, t }:
  { projectId: string; t: StudioStrings }): JSX.Element {
  const [items, setItems] = useState<Row[]>([]);
  const [packages, setPackages] = useState<Row[]>([]);
  const [active, setActive] = useState<Row | null>(null);
  const [section, setSection] = useState("checklist");
  const [detail, setDetail] = useState<Row[]>([]);
  const [gate, setGate] = useState<Row | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState({ title: "", review_type: "launch_readiness_review",
    market: "", artwork_package_id: "" });

  const refresh = useCallback(async () => {
    const [r, pk] = await Promise.all([
      getComplianceCases(projectId), getArtworkPackages(projectId)]);
    if (r.outcome === "ok") setItems(r.data?.items ?? []);
    else setNotice(r.status === 403 ? t.permissionDenied : t.loadError);
    if (pk.outcome === "ok") setPackages(pk.data?.items ?? []);
  }, [projectId, t.permissionDenied, t.loadError]);

  const loadSection = useCallback(async (c: Row, sec: string) => {
    setActive(c); setSection(sec);
    const cid = String(c.id);
    if (sec === "gate") {
      const g = await getComplianceGate(projectId, cid);
      setGate(g.outcome === "ok" ? (g.data as Row) : null); setDetail([]); return;
    }
    const call = sec === "checklist" ? getComplianceChecklist
      : sec === "claims" ? getComplianceClaims
      : sec === "language" ? getComplianceLanguage
      : sec === "signoff" ? getComplianceSignoffs : getComplianceExports;
    const r = await call(projectId, cid);
    setDetail(r.outcome === "ok" ? r.data?.items ?? [] : []);
  }, [projectId]);

  useEffect(() => { void refresh(); }, [refresh]);

  async function onCreate(): Promise<void> {
    if (form.title.trim().length < 2) { setNotice(t.loadError); return; }
    const r = await createComplianceCase(projectId, {
      ...form, artwork_package_id: form.artwork_package_id || null });
    setNotice(r.outcome === "ok" ? null
      : r.status === 403 ? t.permissionDenied : r.errorMessage);
    if (r.outcome === "ok") setForm({ ...form, title: "" });
    await refresh();
  }

  async function onEvaluate(requestedGate: string): Promise<void> {
    if (!active) return;
    const r = await evaluateComplianceGate(projectId, String(active.id), requestedGate);
    setNotice(r.outcome === "ok" ? `${t.finalGate}: ${r.data?.gate}` : r.errorMessage);
    await loadSection(active, "gate"); await refresh();
  }

  async function onDecide(sid: string, status: string): Promise<void> {
    if (!active) return;
    const notes = status === "approved" ? "approved" :
      window.prompt(t.notesLabel) ?? "";
    const r = await decideComplianceSignoff(projectId, String(active.id), sid, status, notes);
    setNotice(r.outcome === "ok" ? null : r.errorMessage);
    await loadSection(active, "signoff"); await refresh();
  }

  return (
    <section data-testid="project-compliance">
      <p className="po-disclaimer" data-testid="compliance-honesty">
        {t.notLegalApproval}
      </p>
      {notice ? <p role="alert" className="po-error">{notice}</p> : null}

      <div className="po-row" data-testid="compliance-create">
        <input placeholder={t.nameLabel} value={form.title}
               data-testid="compliance-title"
               onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <select value={form.review_type} aria-label={t.reviewType}
                onChange={(e) => setForm({ ...form, review_type: e.target.value })}>
          {REVIEW_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <input placeholder="market" value={form.market}
               onChange={(e) => setForm({ ...form, market: e.target.value })} />
        <select value={form.artwork_package_id} aria-label={t.artworkTab}
                onChange={(e) => setForm({ ...form, artwork_package_id: e.target.value })}>
          <option value="">—</option>
          {packages.map((p) => (
            <option key={String(p.id)} value={String(p.id)}>{String(p.title)}</option>))}
        </select>
        <Button variant="primary" onClick={() => void onCreate()}>
          {t.newComplianceCase}
        </Button>
      </div>

      {items.length === 0 ? <p data-testid="compliance-empty">{t.emptyList}</p> : (
        <table className="po-table" data-testid="compliance-list">
          <thead><tr><th>{t.nameLabel}</th><th>{t.reviewType}</th>
            <th>{t.riskLevel}</th><th>{t.finalGate}</th><th>{t.statusLabel}</th></tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={String(c.id)} style={{ cursor: "pointer" }}
                  onClick={() => void loadSection(c, "checklist")}>
                <td>{String(c.title)}</td><td>{String(c.review_type)}</td>
                <td><StatusPill tone={c.risk_level === "critical" || c.risk_level === "high"
                  ? "warn" : "info"} label={String(c.risk_level)} /></td>
                <td><StatusPill tone={String(c.final_gate).startsWith("allowed")
                  ? "ok" : "info"} label={String(c.final_gate)} /></td>
                <td>{String(c.status)}</td>
              </tr>))}
          </tbody>
        </table>)}

      {active ? (
        <div data-testid="compliance-detail">
          <h2>{String(active.title)}</h2>
          {Array.isArray(active.gate_reasons) && active.gate_reasons.length ? (
            <p data-testid="compliance-blockers">🚫 {(active.gate_reasons as string[])
              .slice(0, 4).join(" · ")}</p>) : null}
          <nav className="po-tabs">
            {[["checklist", t.checklistSection], ["claims", t.claimsSection],
              ["language", t.languageSection], ["signoff", t.signoffSection],
              ["gate", t.gateSection], ["exports", t.exportsSection]].map(([k, label]) => (
              <button key={k} type="button" aria-pressed={section === k}
                      className={section === k ? "po-tab po-tab--active" : "po-tab"}
                      onClick={() => void loadSection(active, k)}>{label}</button>))}
          </nav>

          {section === "checklist" ? (
            <ul data-testid="compliance-checklist">
              {detail.map((c) => (
                <li key={String(c.id)}>
                  <StatusPill tone={c.severity === "critical" || c.severity === "high"
                    ? "warn" : "info"} label={String(c.severity)} />{" "}
                  [{String(c.category)}] {String(c.item)}{" "}
                  <select value={String(c.status)}
                          onChange={(e) => void patchComplianceChecklist(projectId,
                            String(active.id), String(c.id), e.target.value)
                            .then(() => loadSection(active, "checklist")).then(refresh)}>
                    {CHK_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </li>))}
            </ul>) : null}

          {section === "claims" ? (
            <ul data-testid="compliance-claims">
              {detail.length === 0 ? <li>{t.emptyList}</li> : detail.map((c) => (
                <li key={String(c.id)}>
                  <StatusPill tone={c.risk_level === "critical" || c.risk_level === "high"
                    ? "warn" : "info"} label={String(c.risk_level)} />{" "}
                  <strong>{String(c.claim_text)}</strong> [{String(c.claim_category)}]{" "}
                  <select value={String(c.status)}
                          onChange={(e) => void patchComplianceClaim(projectId,
                            String(active.id), String(c.id), { status: e.target.value })
                            .then(() => loadSection(active, "claims")).then(refresh)}>
                    {CLAIM_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </li>))}
            </ul>) : null}

          {section === "language" ? (
            <ul data-testid="compliance-language">
              {detail.map((l) => (
                <li key={String(l.id)}>
                  [{String(l.language)}/{String(l.issue_type)}]{" "}
                  <select value={String(l.status)}
                          onChange={(e) => void patchComplianceLanguage(projectId,
                            String(active.id), String(l.id), e.target.value)
                            .then(() => loadSection(active, "language"))}>
                    {["draft", "needs_translation_review", "needs_cultural_review",
                      "accepted", "rejected"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </li>))}
            </ul>) : null}

          {section === "signoff" ? (
            <div data-testid="compliance-signoff">
              <div className="po-row">
                {APPROVAL_TYPES.map((a) => (
                  <Button key={a} variant="secondary" onClick={() =>
                    void requestComplianceSignoff(projectId, String(active.id), a)
                      .then(() => loadSection(active, "signoff"))}>
                    + {a}
                  </Button>))}
              </div>
              <ul>
                {detail.map((s) => (
                  <li key={String(s.id)}>
                    {String(s.approval_type)} —{" "}
                    <StatusPill tone={s.status === "approved" ? "ok"
                      : s.status === "rejected" ? "warn" : "info"}
                                label={String(s.status)} />
                    {s.status === "pending" ? (
                      <> <Button variant="secondary"
                             onClick={() => void onDecide(String(s.id), "approved")}>✓</Button>
                        {" "}<Button variant="secondary"
                             onClick={() => void onDecide(String(s.id), "rejected")}>✗</Button></>
                    ) : s.decision_notes ? ` · ${String(s.decision_notes)}` : null}
                  </li>))}
              </ul>
            </div>) : null}

          {section === "gate" ? (
            <div data-testid="compliance-gate">
              <p>{t.finalGate}: <strong>{String(gate?.gate ?? active.final_gate)}</strong></p>
              {Array.isArray(gate?.reasons) && (gate!.reasons as string[]).length ? (
                <ul>{(gate!.reasons as string[]).map((r, i) => <li key={i}>🚫 {r}</li>)}</ul>) : null}
              <div className="po-row">
                {GATES.map((g) => (
                  <Button key={g} variant="secondary" onClick={() => void onEvaluate(g)}>
                    {g}
                  </Button>))}
              </div>
              <p className="po-disclaimer">{t.notLegalApproval}</p>
            </div>) : null}

          {section === "exports" ? (
            <div data-testid="compliance-exports">
              {["markdown", "json"].map((x) => (
                <Button key={x} variant="secondary" onClick={() =>
                  void createComplianceExport(projectId, String(active.id), x)
                    .then(() => loadSection(active, "exports"))}>{x}</Button>))}
              <ul>
                {detail.map((e) => (
                  <li key={String(e.id)}>
                    {String(e.export_type)} · {String(e.created_at).slice(0, 16)}
                    {e.artifact_id ? (
                      <> · <a href={`/app/artifacts?focus=${e.artifact_id}`}>artifact</a></>) : null}
                  </li>))}
              </ul>
            </div>) : null}
        </div>) : null}
    </section>
  );
}
